import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface UserData {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  rolename?: string;
}

export interface WhiteLabel {
  id: string;
  brandLogo: string;
  brandColor: string;
  textColor: string;
  bgColor: string;
  domainURL: string;
  termsOfServiceURL: string;
  privacyPolicyURL: string;
}

export interface Subscription {
  id: string;
  planName?: string;
  siteQuotaLimit?: number;
  siteQuotaUsed?: number;
  fleetQuotaLimit?: number;
  fleetQuotaUsed?: number;
  plan?: string;
  status?: string;
}

export interface User {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  isActive: boolean;
  profileImg?: string | null;
  mobileNo?: string | null;
  role: {
    id: string;
    rolename: string;
  };
  subscription: Subscription | null;
  whiteLabel?: WhiteLabel | null;
}

export interface UserApiResponse {
  success: boolean;
  message: string;
  data?: {
    user: User;
  };
}

export interface UsersApiResponse {
  success: boolean;
  message: string;
  data?: {
    users: User[];
    total: number;
    page: number;
    pages: number;
  };
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// CREATE USER ACTION
export const createUser = createAsyncThunk<
  UserApiResponse,
  UserData,
  { rejectValue: string }
>("userManagement/createUser", async (formData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.post<UserApiResponse>(
      "/users/create-new-user",
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (response.data.success) {
      toast.success(response.data.message || "User created successfully");
      return response.data;
    } else {
      toast.error(response.data.message || "User creation failed");
      return rejectWithValue(response.data.message || "User creation failed");
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as UserApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET ALL USERS ACTION
export const getAllUsers = createAsyncThunk<
  UsersApiResponse,
  { page?: number; limit?: number; role?: string },
  { rejectValue: string }
>(
  "userManagement/getAllUsers",
  async ({ page, limit, role }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (page !== undefined) queryParams.append("page", page.toString());
      if (limit !== undefined) queryParams.append("limit", limit.toString());
      if (role) queryParams.append("role", role);

      const query = queryParams.toString() ? `?${queryParams.toString()}` : "";
      const response = await createApiClient.get<UsersApiResponse>(
        `/users${query}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to retrieve users");
        return rejectWithValue(
          response.data.message || "Failed to retrieve users"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as UsersApiResponse).message || "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET USER BY ID ACTION
export const getUserById = createAsyncThunk<
  UserApiResponse,
  string,
  { rejectValue: string }
>("userManagement/getUserById", async (id, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<UserApiResponse>(
      `/users/${id}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve user");
      return rejectWithValue(
        response.data.message || "Failed to retrieve user"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as UserApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// UPDATE USER ACTION
export const updateUserById = createAsyncThunk<
  UserApiResponse,
  { id: string; data: Partial<UserData> },
  { rejectValue: string }
>(
  "userManagement/updateUserById",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.put<UserApiResponse>(
        `/users/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "User updated successfully");
        return response.data;
      } else {
        toast.error(response.data.message || "User update failed");
        return rejectWithValue(response.data.message || "User update failed");
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as UserApiResponse).message || "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// TOGGLE USER ACTIVE STATUS ACTION
export const toggleUserActive = createAsyncThunk<
  UserApiResponse,
  string,
  { rejectValue: string }
>("userManagement/toggleUserActive", async (id, { rejectWithValue }) => {
  try {
    const response = await createApiClient.put<UserApiResponse>(
      `/users/toggle-active/${id}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (response.data.success) {
      toast.success(
        response.data.message || "User status toggled successfully"
      );
      return response.data;
    } else {
      toast.error(response.data.message || "User status toggle failed");
      return rejectWithValue(
        response.data.message || "User status toggle failed"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as UserApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});
